import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleMap, getFeaturedArticles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

const articleDetailImages: Record<string, string> = {
  'huanqi-xinli-tanxi': 'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20illustration%20of%20two%20intertwined%20silhouettes%20forming%20a%20balanced%20composition%2C%20warm%20earthy%20tones%20with%20terracotta%20and%20cream%2C%20soft%20gradients%2C%20contemplative%20atmosphere%2C%20editorial%20quality&width=1200&height=600&seq=article-psychology-01-detail&orientation=landscape',
  'kaifang-guanxi-qinggan-guanli': 'https://readdy.ai/api/search-image?query=Abstract%20minimalist%20composition%20of%20two%20separate%20but%20connected%20organic%20shapes%20in%20warm%20amber%20and%20soft%20beige%20tones%2C%20representing%20emotional%20balance%20and%20trust%2C%20clean%20white%20background&width=1200&height=600&seq=article-emotion-01-detail&orientation=landscape',
  'xingjiankang-yufang-zhinan': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20medical%20safety%20concept%20illustration%2C%20abstract%20geometric%20shield%20shape%20in%20warm%20sage%20green%20tones%2C%20soft%20cream%20background%2C%20representing%20protection%20and%20wellness&width=1200&height=600&seq=article-health-01-detail&orientation=landscape',
  'yinsi-baohu-shuzi-anquan': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20privacy%20concept%20illustration%2C%20layered%20geometric%20shapes%20in%20warm%20taupe%20and%20soft%20olive%20tones%2C%20representing%20data%20protection%20and%20security%20layers%2C%20minimalist%20clean%20design&width=1200&height=600&seq=article-privacy-01-detail&orientation=landscape',
  'zhongguo-huanqi-falv-jiexi': 'https://readdy.ai/api/search-image?query=Abstract%20legal%20concept%20illustration%2C%20balanced%20scales%20motif%20rendered%20as%20minimalist%20geometric%20forms%20in%20warm%20earth%20tones%20and%20soft%20grey%2C%20representing%20justice%20and%20legal%20boundaries&width=1200&height=600&seq=article-law-01-detail&orientation=landscape',
  'huanqi-quanshu-yu-liyi': 'https://readdy.ai/api/search-image?query=Abstract%20typographic%20pattern%20illustration%20with%20layered%20translucent%20geometric%20shapes%20in%20warm%20terracotta%20and%20cream%20tones%2C%20representing%20language%20and%20culture%2C%20minimalist%20bookish%20aesthetic&width=1200&height=600&seq=article-dictionary-01-detail&orientation=landscape',
  'chuci-tiyan-zhunbei': 'https://readdy.ai/api/search-image?query=Abstract%20illustration%20of%20a%20gentle%20sunrise%20horizon%20with%20soft%20warm%20gradients%20from%20cream%20to%20terracotta%2C%20representing%20new%20beginnings%20and%20anticipation%2C%20minimalist%20composition&width=1200&height=600&seq=article-first-time-01-detail&orientation=landscape',
  'guoji-huanqi-falv-duibi': 'https://readdy.ai/api/search-image?query=Abstract%20world%20map%20concept%20rendered%20as%20interconnected%20geometric%20nodes%20in%20warm%20amber%20and%20olive%20tones%20on%20cream%20background%2C%20representing%20global%20perspectives&width=1200&height=600&seq=article-global-law-01-detail&orientation=landscape',
  'huanqi-shequn-shengtao': 'https://readdy.ai/api/search-image?query=Abstract%20network%20visualization%20with%20interconnected%20organic%20nodes%20in%20warm%20earth%20tones%20and%20soft%20olive%2C%20representing%20community%20connections%2C%20minimalist%20geometric%20design&width=1200&height=600&seq=article-community-01-detail&orientation=landscape',
  'tuiqun-sikao-zongjie': 'https://readdy.ai/api/search-image?query=Abstract%20sunset%20horizon%20illustration%20with%20warm%20fading%20gradients%20from%20amber%20to%20soft%20cream%2C%20representing%20closure%20and%20reflection%2C%20minimalist%20landscape%20composition&width=1200&height=600&seq=article-exit-01-detail&orientation=landscape',
  'qingxu-bianjie-jianli': 'https://readdy.ai/api/search-image?query=Abstract%20composition%20of%20soft%20geometric%20layers%20with%20distinct%20but%20permeable%20boundaries%20in%20warm%20sienna%20and%20cream%20tones%2C%20representing%20emotional%20boundaries&width=1200&height=600&seq=article-boundary-01-detail&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-grow flex items-center justify-center pt-16">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
              <Link to="/" className="text-sm text-primary-600 hover:text-primary-700">返回首页</Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = getFeaturedArticles(3).filter((a) => a.slug !== article.slug).slice(0, 3);
  const articleImageUrl = articleDetailImages[article.slug] || '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: articleImageUrl,
    publisher: {
      '@type': 'Organization',
      name: '换妻一族',
    },
    inLanguage: 'zh-CN',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.example.com/article/${slug}`,
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${article.title} - 换妻一族`}
        description={article.excerpt}
        keywords={`换妻一族,${category ? category.name : ''},${article.title},换妻文化,开放关系`}
        canonicalUrl={`https://www.example.com/article/${slug}`}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-20 pb-12">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category ? category.name : '', href: category ? `/category/${category.slug}` : undefined },
                { label: article.title },
              ]}
            />

            <div className="mt-6 mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-800 mb-4 hover:bg-secondary-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-snug" style={{ fontFamily: 'var(--font-heading)' }}>
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span>{article.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.publishDate}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </div>

            <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-10">
              <img
                src={articleImageUrl}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              style={{
                fontFamily: 'var(--font-body)',
              } as React.CSSProperties}
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-bold text-foreground-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} article={ra} />
                  ))}
                </div>
              </div>
            )}
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}